<template>
  <SubViewWrapper
    title="Completion"
    :hide-header="embedded"
    @back="$emit('back')"
    @close="$emit('close')"
  >
    <div class="content" :class="{ embedded }">
      <div class="field-row">
        <span class="label">Field</span>
        <BaseInputSelect
          class="column-picker"
          select-only
          :options="columnOptions"
          :value="selectedValue"
          placeholder="Pick a field…"
          :disabled="!columnOptions.length"
          @update:value="selectColumn"
        />
      </div>

      <div v-if="selectedColumnKind === 'bool'" class="sentence-row">
        <span class="sentence-text">When the task becomes</span>
        <BaseButton kind="auxiliary" type="tertiary" state="active">
          <template #icon>
            <TaskCheckbox
              status="done"
              :is-interactive="false"
              size="sm"
              class="no-pointer"
            />
          </template>
        </BaseButton>
        <span class="sentence-text">, set to</span>
        <ButtonMenu placement="bottom-start" :is-hidden-on-click="true">
          <template #btn>
            <div class="pick-trigger">
              <TaskCheckbox
                :status="boolDoneValue ? 'done' : 'todo'"
                :is-interactive="false"
                size="sm"
                class="no-pointer"
              />
            </div>
          </template>
          <template #content>
            <div class="column-options">
              <BaseButton
                kind="auxiliary"
                type="tertiary"
                :is-expanded="true"
                :state="boolDoneValue ? 'active' : 'default'"
                @click="setBoolDoneValue(true)"
              >
                <template #icon>
                  <TaskCheckbox
                    status="done"
                    :is-interactive="false"
                    size="sm"
                    class="no-pointer"
                  />
                </template>
              </BaseButton>
              <BaseButton
                kind="auxiliary"
                type="tertiary"
                :is-expanded="true"
                :state="!boolDoneValue ? 'active' : 'default'"
                @click="setBoolDoneValue(false)"
              >
                <template #icon>
                  <TaskCheckbox
                    status="todo"
                    :is-interactive="false"
                    size="sm"
                    class="no-pointer"
                  />
                </template>
              </BaseButton>
            </div>
          </template>
        </ButtonMenu>
      </div>

      <StatusValueMapping
        v-else-if="selectValues"
        :model-value="selectValues"
        :options="valueOptions"
        :property-name="selectedColumnName ?? ''"
        @update:model-value="setSelectValues"
      />

      <div v-if="!embedded" class="actions">
        <BaseButton
          kind="main"
          type="primary"
          text="Save"
          :state="canSave ? 'default' : 'disabled'"
          @click="save"
        />
        <BaseButton
          v-if="statusMapping"
          kind="main"
          type="secondary"
          tint="red"
          text="Clear"
          @click="clear"
        />
      </div>
    </div>
  </SubViewWrapper>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, ref } from "vue";

import type {
  Cell,
  ColumnId,
  FieldWithOrigin,
  GraphDatabase,
  TaskBindingStatusMapping,
  TaskBindingStatusValues,
} from "@ctrl/controller-rpc";

import BaseButton from "@/components/base/BaseButton.vue";
import BaseInputSelect, {
  type SelectOption,
} from "@/components/base/BaseInputSelect.vue";
import ButtonMenu from "@/components/elements/ButtonMenu.vue";
import {
  emptyStatusValueMapping,
  isStatusMappingComplete,
  mappedStatuses,
  valuesReadAs,
  type StatusValueMappingState,
  type StatusValueOption,
} from "@/components/settings/status-value-mapping";
import StatusValueMapping from "@/components/settings/StatusValueMapping.vue";
import TaskCheckbox, {
  type TaskStatus,
} from "@/components/task/TaskCheckbox.vue";
import SubViewWrapper from "@/components/views/config/SubViewWrapper.vue";

function collectSelectOptions(
  value: FieldWithOrigin["value"]
): string[] | null {
  if (value.type === "string_const") return [value.value];
  if (value.type === "null") return [];
  if (value.type !== "variant") return null;
  const collected: string[] = [];
  for (const c of value.cases) {
    const sub = collectSelectOptions(c);
    if (sub === null) return null;
    collected.push(...sub);
  }
  return collected;
}

function isSelectField(field: FieldWithOrigin): boolean {
  const opts = collectSelectOptions(field.value);
  return opts !== null && opts.length > 0;
}

function collectBoolLeaves(value: FieldWithOrigin["value"]): boolean | null {
  if (value.type === "bool") return true;
  if (value.type === "null") return false;
  if (value.type !== "variant") return null;
  let hasBool = false;
  for (const c of value.cases) {
    const sub = collectBoolLeaves(c);
    if (sub === null) return null;
    if (sub) hasBool = true;
  }
  return hasBool;
}

function isBoolField(field: FieldWithOrigin): boolean {
  return collectBoolLeaves(field.value) === true;
}

function getSelectOptions(field: FieldWithOrigin): string[] {
  return collectSelectOptions(field.value) ?? [];
}

type ColumnKind = "select" | "bool";

function fieldKind(field: FieldWithOrigin): ColumnKind | null {
  if (isBoolField(field)) return "bool";
  if (isSelectField(field)) return "select";
  return null;
}

function cellString(c: Cell | undefined): string | null {
  if (c && typeof c === "object" && "type" in c && c.type === "string")
    return c.value;
  return null;
}

function cellBool(c: Cell | undefined): boolean | null {
  if (c && typeof c === "object" && "type" in c && c.type === "bool")
    return c.value;
  return null;
}

function readStringCells(cells: Cell[] | undefined): string[] {
  if (!cells) return [];
  return cells.flatMap((c) => {
    const s = cellString(c);
    return s !== null ? [s] : [];
  });
}

type LocalMapping =
  | { kind: "select"; values: StatusValueMappingState }
  | { kind: "bool"; doneValue: boolean };

function emptyMapping(kind: ColumnKind): LocalMapping {
  return kind === "bool"
    ? { kind: "bool", doneValue: true }
    : { kind: "select", values: emptyStatusValueMapping() };
}

function mappingKind(mapping: TaskBindingStatusMapping): ColumnKind {
  const writes: Array<Cell | undefined> = [
    mapping.done.write,
    mapping.todo.write,
    mapping.discarded.write,
  ];
  for (const w of writes) {
    if (cellBool(w) !== null) return "bool";
  }
  return "select";
}

export function fromStatusMapping(
  mapping: TaskBindingStatusMapping | undefined
): LocalMapping {
  if (!mapping) return emptyMapping("select");
  if (mappingKind(mapping) === "bool") {
    return {
      kind: "bool",
      doneValue: cellBool(mapping.done.write) ?? true,
    };
  }
  const values = emptyStatusValueMapping();
  for (const status of mappedStatuses) {
    const v: TaskBindingStatusValues = mapping[status];
    const write = cellString(v.write);
    if (write !== null) values.writes[status] = write;
    for (const value of readStringCells(v.read)) values.reads[value] = status;
  }
  return { kind: "select", values };
}

export default defineComponent({
  name: "StatusMappingConfig",
  components: {
    BaseButton,
    BaseInputSelect,
    ButtonMenu,
    StatusValueMapping,
    SubViewWrapper,
    TaskCheckbox,
  },
  props: {
    statusMapping: {
      type: Object as PropType<TaskBindingStatusMapping>,
      required: false,
    },
    database: {
      type: Object as PropType<GraphDatabase>,
      required: true,
    },
    statusOptions: {
      type: Array as PropType<StatusValueOption[]>,
      required: false,
      default: () => [],
    },
    columnLabel: {
      type: String,
      required: false,
      default: "",
    },
    /**
     * Rendered inside a host that owns the chrome (title bar and Save / Clear
     * actions, driven through `canSave`, `save` and `clear`).
     */
    embedded: { type: Boolean, default: false },
  },
  emits: {
    "update:statusMapping": (_: TaskBindingStatusMapping | undefined) => true,
    back: () => true,
    close: () => true,
  },
  setup(props, { emit }) {
    const selectedColumnId = ref<ColumnId | null>(
      props.statusMapping?.column ?? null
    );
    const local = ref<LocalMapping>(fromStatusMapping(props.statusMapping));

    const isMappedColumn = computed(
      () =>
        selectedColumnId.value !== null &&
        selectedColumnId.value === props.statusMapping?.column
    );

    const availableColumns = computed<[ColumnId, FieldWithOrigin][]>(() =>
      Object.entries(props.database.type.fields).filter(
        ([id, field]) =>
          fieldKind(field) !== null || id === props.statusMapping?.column
      )
    );

    const selectedField = computed<FieldWithOrigin | null>(() =>
      selectedColumnId.value
        ? (props.database.type.fields[selectedColumnId.value] ?? null)
        : null
    );

    const selectedColumnName = computed<string | null>(
      () =>
        (selectedColumnId.value === props.statusMapping?.column
          ? props.columnLabel
          : "") ||
        selectedField.value?.name ||
        null
    );
    const columnName = (id: ColumnId, field: FieldWithOrigin): string =>
      (id === props.statusMapping?.column ? props.columnLabel : "") ||
      field.name;
    // The mapped column may be missing from the sampled type (integration
    // columns without values yet): it stays selectable under the caller's
    // label, and reads as unset until that label is known.
    const columnOptions = computed<SelectOption[]>(() => {
      const options: SelectOption[] = availableColumns.value.map(
        ([id, field]) => ({
          value: id,
          label: columnName(id, field),
          aliases: [],
        })
      );
      const mapped = props.statusMapping?.column;
      if (
        mapped !== undefined &&
        props.columnLabel &&
        !options.some((o) => o.value === mapped)
      ) {
        options.push({ value: mapped, label: props.columnLabel, aliases: [] });
      }
      return options;
    });
    /** The picker's value: empty while the selected column is unknown. */
    const selectedValue = computed<string>(() =>
      columnOptions.value.some((o) => o.value === selectedColumnId.value)
        ? (selectedColumnId.value ?? "")
        : ""
    );

    // Integration-managed columns (Notion's `routine_status` and friends) hold
    // provider values in a column Routine only knows as a plain string, so the
    // saved mapping is the only place its values are written down.
    const mappedValues = computed<string[]>(() => {
      const mapping = props.statusMapping;
      if (!mapping || !isMappedColumn.value) return [];
      const values = new Set<string>();
      for (const status of mappedStatuses) {
        const write = cellString(mapping[status].write);
        if (write !== null) values.add(write);
        for (const value of readStringCells(mapping[status].read))
          values.add(value);
      }
      return [...values];
    });

    const selectedOptions = computed<string[]>(() => {
      if (isMappedColumn.value && props.statusOptions.length > 0) {
        return props.statusOptions.map((option) => option.value);
      }
      if (selectedField.value && isSelectField(selectedField.value)) {
        return getSelectOptions(selectedField.value);
      }
      return mappedValues.value;
    });

    const selectedColumnKind = computed<ColumnKind | null>(() => {
      const fromField = selectedField.value
        ? fieldKind(selectedField.value)
        : null;
      if (selectedValue.value === "") return null;
      if (fromField !== null) return fromField;
      return selectedOptions.value.length > 0 ? "select" : null;
    });
    const optionName = (value: string): string =>
      props.statusOptions.find((option) => option.value === value)?.label ??
      value;

    const valueOptions = computed<StatusValueOption[]>(() =>
      selectedOptions.value.map((value) => ({
        value,
        label: optionName(value),
      }))
    );

    const selectValues = computed<StatusValueMappingState | null>(() =>
      local.value.kind === "select" && selectedColumnKind.value === "select"
        ? local.value.values
        : null
    );

    const setSelectValues = (values: StatusValueMappingState) => {
      local.value = { kind: "select", values };
    };

    const boolDoneValue = computed<boolean>(() =>
      local.value.kind === "bool" ? local.value.doneValue : true
    );

    const selectColumn = (id: ColumnId) => {
      if (selectedColumnId.value !== id) {
        selectedColumnId.value = id;
        const field = props.database.type.fields[id];
        const kind = (field && fieldKind(field)) ?? "select";
        local.value = emptyMapping(kind);
      }
    };

    const setBoolDoneValue = (value: boolean) => {
      local.value = { kind: "bool", doneValue: value };
    };

    const canSave = computed<boolean>(() => {
      if (selectedValue.value === "") return false;
      if (local.value.kind === "bool") return true;
      return isStatusMappingComplete(local.value.values);
    });

    const stringCell = (value: string): Cell => ({ type: "string", value });
    const boolCell = (value: boolean): Cell => ({ type: "bool", value });

    const save = () => {
      const col = selectedColumnId.value;
      if (!col) return;
      if (local.value.kind === "bool") {
        const doneValue = local.value.doneValue;
        const other = !doneValue;
        emit("update:statusMapping", {
          column: col,
          kind: "values",
          done: { read: [boolCell(doneValue)], write: boolCell(doneValue) },
          todo: { read: [boolCell(other)], write: boolCell(other) },
          discarded: { read: [], write: boolCell(doneValue) },
        });
      } else {
        const values = local.value.values;
        const { todo, done, discarded } = values.writes;
        if (todo === undefined || done === undefined || discarded === undefined)
          return;
        // Values only known from the saved mapping (the column's sampled type
        // may not list every option) keep their read status.
        const known = [
          ...new Set([...selectedOptions.value, ...Object.keys(values.reads)]),
        ];
        const statusValues = (
          status: TaskStatus,
          write: string
        ): TaskBindingStatusValues => ({
          read: valuesReadAs(values, known, status).map(stringCell),
          write: stringCell(write),
        });
        emit("update:statusMapping", {
          column: col,
          kind: "values",
          todo: statusValues("todo", todo),
          done: statusValues("done", done),
          discarded: statusValues("discarded", discarded),
        });
      }
      emit("back");
    };

    const clear = () => {
      emit("update:statusMapping", undefined);
      emit("back");
    };

    return {
      columnOptions,
      selectedColumnId,
      selectedValue,
      selectedColumnName,
      selectedColumnKind,
      valueOptions,
      selectValues,
      setSelectValues,
      boolDoneValue,
      selectColumn,
      setBoolDoneValue,
      canSave,
      save,
      clear,
    };
  },
});
</script>

<style scoped lang="scss">
@use "../../styles/settings.scss";

.content {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 16px;

  &.embedded {
    padding: 0;
  }
}

.field-row {
  display: flex;
  align-items: center;
  gap: 12px;

  .label {
    @extend %font-body-medium;
    flex-shrink: 0;
    width: 64px;
  }

  .column-picker {
    flex: 1;
  }
}

.sentence-row {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}

.sentence-text {
  @extend %font-body-regular;

  @include themed() {
    color: t($grey-60);
  }
}

.column-options {
  display: flex;
  flex-direction: column;
  min-width: 200px;
  padding: 4px;
  gap: 2px;
}

.no-pointer {
  pointer-events: none;
}

.pick-trigger {
  display: flex;
  align-items: center;
  padding: 3px 6px;
  border-radius: $border-radius-m;
  cursor: pointer;

  @include themed() {
    border: 1px solid t($grey-30);
    color: t($grey-80);
  }

  &:hover {
    @include themed() {
      border-color: t($grey-50);
    }
  }
}

.actions {
  display: flex;
  gap: 8px;
}
</style>
